import * as React from "react";
import {useEffect, useState} from "react";
import {Box, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography} from "@mui/material";
import {Add, DeleteOutline, UploadFileOutlined} from "@mui/icons-material";
import GenericDialog from "../../../../../components/dialog/generic-dialog.component";
import {translate} from "../../../../../translate/translate";
import {BaseService} from "../../../../../services/base-service";
import {URLS} from "../../../../../services/app-urls";
import {Client} from "../../../../../models/basic/client";
import {Project} from "../../../../../models/basic/project";
import {NpiChecklist} from "../../../../../models/npi/npi-checklist";
import {toast} from "react-toastify";
import {ErrorComponent} from "../../../../../components/error/error.component";

interface NewNpiChecklistDialogProps {
    open: boolean;
    onClose: () => void;
    onCreated?: (checklist: NpiChecklist, clientId: number) => void;
}

const menuProps = {
    PaperProps: {
        style: {maxHeight: 300},
    },
};

const NewNpiChecklistDialogComponent: React.FC<NewNpiChecklistDialogProps> = ({open, onClose, onCreated}) => {
    const clientService = new BaseService<Client>(URLS.CLIENT);
    const projectService = new BaseService<Project>(URLS.PROJECT);
    const checklistService = new BaseService<any>(URLS.NPI_CHECKLIST);

    const [clients, setClients] = useState<Client[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);

    const [client, setClient] = useState<string>("");
    const [project, setProject] = useState<string>("");
    const [partNumberInput, setPartNumberInput] = useState<string>("");
    const [partNumbers, setPartNumbers] = useState<string[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        clientService.clearParameters();
        clientService.addParameter("active", "true");
        clientService.getAll().then(response => setClients(response.data));
    }, [open]);

    useEffect(() => {
        setProject("");
        setProjects([]);
        if (!client) {
            return;
        }
        projectService.clearParameters();
        projectService.addParameter("client", client);
        projectService.addParameter("active", "true");
        projectService.getAll().then(response => setProjects(response.data));
    }, [client]);

    const handleAddPartNumber = () => {
        const partNumber = partNumberInput.trim();
        const isDuplicate = partNumbers.includes(partNumber);
        if (!partNumber) {
            return;
        }
        if (isDuplicate) {
            toast.error(translate.t("part_number_already_added"));
            return;
        }
        setPartNumbers([...partNumbers, partNumber]);
        setPartNumberInput("");
    };

    const handleRemovePartNumber = (partNumber: string) => {
        setPartNumbers(partNumbers.filter(item => item !== partNumber));
    };

    const handlePartNumberKey = (event: React.KeyboardEvent) => {
        const isNavigationKey = event.key.startsWith("Arrow") || ["Tab", "Home", "End", "Shift", "Control", "Meta", "Alt"].includes(event.key);
        const isCopyShortcut = (event.ctrlKey || event.metaKey) && ["c", "a"].includes(event.key.toLowerCase());
        if (isNavigationKey || isCopyShortcut) {
            return;
        }
        toast.info(translate.t("part_number_read_only"), {toastId: "part_number_read_only"});
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        const maxSizeInBytes = 5 * 1024 * 1024;
        event.target.value = "";
        if (!selectedFile) {
            return;
        }
        const isExcel = /\.(xls|xlsx)$/i.test(selectedFile.name);
        if (!isExcel) {
            toast.error(translate.t("invalid_file"));
            return;
        }
        const exceedsMaxSize = selectedFile.size > maxSizeInBytes;
        if (exceedsMaxSize) {
            toast.error(translate.t("file_exceeds_max_size"));
            return;
        }
        setFile(selectedFile);
    };

    const handleRemoveFile = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setFile(null);
    };

    const handleClose = () => {
        setClient("");
        setProject("");
        setPartNumberInput("");
        setPartNumbers([]);
        setFile(null);
        onClose();
    };

    const handleSubmit = () => {
        if (saving) {
            return;
        }
        setSaving(true);
        checklistService.clearParameters();
        checklistService.save({
            project: Number(project),
            part_numbers: JSON.stringify(partNumbers),
            template_file: file,
            active: true,
        }, true).then((response) => {
            toast.success(translate.t("successfully_registered"));
            onCreated?.(response.data, Number(client));
            handleClose();
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setSaving(false);
        });
    };

    return (
        <GenericDialog
            open={open}
            onClose={handleClose}
            title={"new_npi_checklist"}
            colorButton={"primary"}
            labelButton={"save_form"}
            closeButtonText={"cancel"}
            disableConfirm={!project || partNumbers.length === 0 || !file}
            loading={saving}
            handleConfirm={handleSubmit}>
            <Grid container spacing={2} width="100%">
                <Grid size={12}>
                    <FormControl fullWidth>
                        <InputLabel id="checklist-client-label" required>{translate.t("client")}</InputLabel>
                        <Select
                            MenuProps={menuProps}
                            labelId="checklist-client-label"
                            label={`${translate.t("client")}*`}
                            value={client}
                            onChange={(event) => setClient(event.target.value)}>
                            {clients.map((item) => (<MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth disabled={!client}>
                        <InputLabel id="checklist-project-label" required>{translate.t("product_project_name")}</InputLabel>
                        <Select
                            MenuProps={menuProps}
                            labelId="checklist-project-label"
                            label={`${translate.t("product_project_name")}*`}
                            value={project}
                            onChange={(event) => setProject(event.target.value)}>
                            {projects.map((item) => (<MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={12} sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <TextField
                        fullWidth
                        required
                        label={translate.t("part_number")}
                        placeholder={translate.t("insert_part_number")}
                        value={partNumberInput}
                        slotProps={{htmlInput: {maxLength: 256}, inputLabel: {shrink: true}}}
                        onChange={(event) => setPartNumberInput(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                handleAddPartNumber();
                            }
                        }}
                    />
                    <IconButton color="primary" onClick={handleAddPartNumber}>
                        <Add/>
                    </IconButton>
                </Grid>
                {partNumbers.map((partNumber) => (
                    <Grid key={partNumber} size={12} sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <Tooltip title={translate.t("part_number_read_only")} placement="top" arrow>
                            <TextField
                                fullWidth
                                value={partNumber}
                                slotProps={{input: {readOnly: true}}}
                                onKeyDown={handlePartNumberKey}
                                sx={{
                                    "& .MuiInputBase-root": {backgroundColor: "#F5F5F5", cursor: "default"},
                                    "& .MuiInputBase-input": {cursor: "default", caretColor: "transparent", color: "rgba(0, 0, 0, 0.5)"},
                                    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgba(0, 0, 0, 0.12)",
                                        borderWidth: "1px",
                                    },
                                }}
                            />
                        </Tooltip>
                        <IconButton color="primary" onClick={() => handleRemovePartNumber(partNumber)}>
                            <DeleteOutline/>
                        </IconButton>
                    </Grid>
                ))}
                <Grid size={12}>
                    <Typography sx={{fontWeight: 600, mb: 1}}>{translate.t("checklist_template")}</Typography>
                    <Box
                        component="label"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                            p: 3,
                            border: "1px dashed #E2E8F0",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}>
                        <input hidden type="file" accept=".xls,.xlsx" onChange={handleFileChange}/>
                        <UploadFileOutlined color="primary"/>
                        {file && (
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, maxWidth: "100%"}}>
                                <Tooltip title={file.name} arrow>
                                    <Typography
                                        sx={{color: "#003FFB", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                        {file.name}
                                    </Typography>
                                </Tooltip>
                                <Tooltip title={translate.t("remove_file")} arrow>
                                    <IconButton size="small" color="primary" onClick={handleRemoveFile}>
                                        <DeleteOutline fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        {!file && (
                            <Typography>
                                <Box component="span" sx={{color: "#003FFB", textDecoration: "underline"}}>
                                    {translate.t("click_to_add_file")}
                                </Box>
                                <Box component="span" sx={{color: "#606369"}}> {translate.t("excel_max_size")}</Box>
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </GenericDialog>
    );
};

export default NewNpiChecklistDialogComponent;